import { MILLISECONDS_PER_DAY } from "./keys";

// - - - - - - - - - -
// Pure next-business-day helper for issue #138 — "Red highlight on tomorrow's
// empty cells in Optimizer."
//
// "Tomorrow" is the next Mon–Fri date after the given date (default = now).
// Strict M-F math: no holiday calendar. Holidays are handled separately by
// bulk-stamping OFF attributes on all installers for the holiday date — when
// an installer has an OFF attribute on tomorrow's date, the cell-level
// highlight check in highlightEmptyTomorrowCell.js excludes that cell from
// the red treatment.
//
// Mapping (today → "tomorrow column"):
//   Mon → Tue
//   Tue → Wed
//   Wed → Thu
//   Thu → Fri
//   Fri → Mon (skip Sat, Sun)
//   Sat → Mon (skip Sun)
//   Sun → Mon
//
// The +43200000 (12h) DST cushion mirrors weekDates.js — see header comment
// there for the rationale (DST boundaries can leave +1 day arithmetic at
// 23:00 of the prior day; the cushion absorbs that).

const snapToLocalMidnight = (d) => {
  const result = new Date(d);
  result.setHours(0, 0, 0, 0);
  return result;
};

const isWeekend = (date) => {
  const day = date.getDay();
  return day === 0 || day === 6;
};

/**
 * Returns the next business day (Mon–Fri) AFTER the given date as a Date
 * object snapped to local midnight. Default `fromDate` = now.
 */
export const getNextBusinessDay = (fromDate = new Date()) => {
  const start = snapToLocalMidnight(fromDate);
  let candidate = snapToLocalMidnight(
    new Date(start.getTime() + MILLISECONDS_PER_DAY + 43200000)
  );
  while (isWeekend(candidate)) {
    candidate = snapToLocalMidnight(
      new Date(candidate.getTime() + MILLISECONDS_PER_DAY + 43200000)
    );
  }
  return candidate;
};

/**
 * Returns the next business day as an en-us locale date string ("M/D/YYYY")
 * to match the format produced by buildWeekDatesArray() in weekDates.js.
 * Callers can compare this directly against weekDatesArray entries.
 */
export const getNextBusinessDayDateString = (fromDate = new Date()) =>
  getNextBusinessDay(fromDate).toLocaleDateString("en-us");
